import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IconProps } from 'shared';

import media from '../../utils/Breakpoints';
import { fonts, colors, boxshadow } from '../../utils/config';


const StyledCategoryItem = styled.li`
	position: relative;
	height: 80px;
	width: 100%;
	border: 2px solid transparent;
	padding: 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: ${colors.white};

	&::after {
		position: absolute;
		left: 4px;
		right: 4px;
		bottom: -2px;
		content: '';
		display: block;
		height: 1px;
		background-color: ${colors.graylight};
	}

	&:hover,
	&:focus {
		cursor: pointer;
		border-radius: 5px;
		border-color: ${colors.cyandark};
		outline: none;

		&::after {
			display: none;
		}
	}

	${props =>
		props.active &&
		`
			border-color: ${colors.cyandark};
	`}

	${media.md`
		height: 80px;
		width: 358px;
		padding: 16px;
		box-shadow: ${props => (props.hideShadow ? 'none' : `${boxshadow.default}`)};
		border-radius: 5px;
		margin-bottom: 8px;

		&::after {
			display: none;
		}
	`}

	${media.lg`
		height: 96px;
		width: 458px;
		padding: 24px;
	`}
`;

const TextWrapper = styled.div`
	max-width: 290px;

	${media.md`
		max-width: 250px;
	`}


	${media.lg`
		max-width: 290px;
	`}

	${props =>
		props.listtype === 'stops' &&
		`
			max-width: 340px;
	`}
`;

const Title = styled.h2`
	font-family: ${fonts.default};
	font-weight: normal;
	font-size: 16px;
	line-height: 21px;

	${media.md`
		font-size: 18px;
		line-height: 23px;
	`}
`;

const Subtitle = styled.p`
	font-size: 14px;
	line-height: 18px;
	color: ${colors.graydark};

	${media.md`
		font-size: 18px;
		line-height: 23px;
	`}
`;

const MediaWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: ${props => (props.image ? '56px' : '48px')};
	height: ${props => (props.image ? '56px' : '48px')};

	${media.md`
		width: ${props => (props.image ? '64px' : '48px')};
		height: ${props => (props.image ? '64px' : '48px')};
		margin-right: ${props => (props.image ? '0' : null)};
	`}

	${media.lg`
		width: ${props => (props.image ? '80px' : '48px')};
		height: ${props => (props.image ? '80px' : '48px')};
		margin-right: ${props => (props.image ? '-8px' : null)};
	`}

	img {
		display: block;
		width: 56px;
		height: 56px;
		overflow: hidden;
		border-radius: 5px;
		background-color: ${colors.graylight};
		object-fit: cover;

		${media.md`
			width: 64px;
			height: 64px;
			margin-right: -16px;
		`}

		${media.lg`
			width: 80px;
			height: 80px;
			margin-right: -18px;
		`}
	}
`;

export default function CategoryItem({
	id,
	title,
	subtitle,
	icon,
	image,
	active,
	hideShadow,
	listtype,
	type,
	onClick,
}) {
	let mediaItem = '';

	if (
		[
			'freizeit',
			'essentrinken',
			'kultur',
			'sehenswuerdigkeiten',
			'rundumsrad',
			'bildungseinrichtungen',
			'krankenhaeuserkliniken',
			'verbaendevereine',
			'stella',
			'car2go',
			'sharenow',
			'flinkster',
			'regiorad',
			'stadtmobil',
		].indexOf(icon) > -1
	) {
		mediaItem = (
			<svg width="100%" height="100%" viewBox={IconProps[icon].viewBox}>
				<path d={IconProps[icon].path} fill={colors.grayblue} />
			</svg>
		);
	} else if (icon === 'stop') {
		mediaItem = (
			<svg width="36" height="36" viewBox={IconProps[icon].viewBox}>
				<circle stroke="#009110" strokeWidth="3" fill="#FFF200" cx="18" cy="18" r="16.5" />
				<path d={IconProps[icon].path} fill="#009110" />
			</svg>
		);
	} else if (image && image !== '') {
		mediaItem = <img src={image} alt={title} width="80" height="80" />;
	} else {
		mediaItem = '';
	}

	const onFocus = () => {
		const marker = document.querySelector(`.marker--${type}[data-vvsid="${id}"]`);
		if (marker) {
			marker.classList.add('active');
		}
	};

	const onBlur = () => {
		const marker = document.querySelector(`.marker--${type}[data-vvsid="${id}"]`);
		if (marker) {
			marker.classList.remove('active');
		}
	};

	return (
		<StyledCategoryItem
			data-id={id}
			active={active}
			onMouseOver={onFocus}
			onFocus={onFocus}
			onMouseOut={onBlur}
			onBlur={onBlur}
			onClick={() => {
				onBlur();
				onClick();
			}}
			hideShadow={hideShadow}
			tabIndex={0}
		>
			<TextWrapper listtype={listtype}>
				<Title>{title}</Title>
				<Subtitle>{subtitle}</Subtitle>
			</TextWrapper>
			<MediaWrapper image={image}>{mediaItem}</MediaWrapper>
		</StyledCategoryItem>
	);
}

CategoryItem.propTypes = {
	id: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	subtitle: PropTypes.string,
	icon: PropTypes.oneOf([
		'freizeit',
		'essentrinken',
		'kultur',
		'sehenswuerdigkeiten',
		'rundumsrad',
		'bildungseinrichtungen',
		'krankenhaeuserkliniken',
		'verbaendevereine',
		'stella',
		'car2go',
		'flinkster',
		'regiorad',
		'stadtmobil',
		'stop',
		'poi',
	]),
	image: PropTypes.string,
	listtype: PropTypes.string,
	active: PropTypes.bool,
	hideShadow: PropTypes.bool,
	type: PropTypes.string,
	onClick: PropTypes.func,
};
